import { useEffect, useRef, useState } from 'react';

/**
 * Extract a YouTube video ID from any common URL form:
 *   - https://www.youtube.com/embed/ID
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/shorts/ID
 */
export function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const s = String(url);
    let m = s.match(/(?:embed|shorts)\/([A-Za-z0-9_-]{6,})/);
    if (m) return m[1];
    m = s.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
    if (m) return m[1];
    m = s.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (m) return m[1];
  } catch { /* ignore */ }
  return null;
}

/**
 * Preflight embeddability check via the public oEmbed endpoint.
 * Returns true if YT confirms the video can be embedded, false otherwise.
 * CORS is permitted on oembed by YouTube.
 */
export async function checkYouTubeEmbeddable(videoId) {
  if (!videoId) return false;
  try {
    const url = `https://www.youtube.com/oembed?url=https%3A//www.youtube.com/watch%3Fv%3D${videoId}&format=json`;
    const res = await fetch(url, { method: 'GET', mode: 'cors' });
    return res.ok; // 200 = embeddable, 401/404 = not
  } catch {
    return false;
  }
}

let ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') { try { prev(); } catch { /* ignore */ } }
      resolve(window.YT);
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('YT API load failed'));
    document.head.appendChild(script);
  });
  return ytApiPromise;
}

/**
 * Attach a YouTube IFrame Player to `containerRef` and expose imperative controls.
 * The container must be an empty div; YT will replace it with an iframe.
 *
 * Returns getCurrentTime() with linear interpolation between polls so consumers
 * driving animations (chord pills, waveform playheads…) get sub-frame-smooth values.
 */
export default function useYouTubePlayer(videoId, containerRef) {
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  // Interpolation state: last polled YT time + wall clock at that moment + rate + isPlaying
  const timeRef = useRef({ polledAt: 0, polledTime: 0, playing: false, rate: 1 });

  useEffect(() => {
    setReady(false);
    setError(false);
    timeRef.current = { polledAt: 0, polledTime: 0, playing: false, rate: 1 };
    if (!videoId || !containerRef.current) return;

    let cancelled = false;
    let localPlayer = null;

    loadYouTubeAPI().then((YT) => {
      if (cancelled || !containerRef.current) return;
      // Ensure a fresh child div for YT to replace (YT.Player consumes the element).
      containerRef.current.innerHTML = '';
      const host = document.createElement('div');
      host.style.width = '100%';
      host.style.height = '100%';
      containerRef.current.appendChild(host);

      localPlayer = new YT.Player(host, {
        videoId,
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            if (cancelled) return;
            playerRef.current = localPlayer;
            setReady(true);
          },
          onStateChange: (e) => {
            // 1 = playing, 2 = paused, 3 = buffering, 0 = ended, -1 = unstarted, 5 = cued
            const st = e?.data;
            const playing = st === 1;
            try {
              timeRef.current.polledTime = localPlayer.getCurrentTime?.() ?? timeRef.current.polledTime;
              timeRef.current.polledAt = performance.now();
              timeRef.current.playing = playing;
              timeRef.current.rate = localPlayer.getPlaybackRate?.() ?? 1;
            } catch { /* ignore */ }
          },
          // Error codes: 2 (bad param), 5 (HTML5 issue), 100 (removed/private),
          // 101 / 150 (embedding disabled by owner) → surface a fallback.
          // 153 (player config) fires here on some builds but not others.
          onError: (e) => {
            if (cancelled) return;
            console.debug('YT.Player error', e?.data);
            setError(true);
            setReady(false);
          },
        },
      });
    }).catch(() => { if (!cancelled) setError(true); });

    return () => {
      cancelled = true;
      try { if (localPlayer && localPlayer.destroy) localPlayer.destroy(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, [videoId, containerRef]);

  // Re-poll the actual YT time periodically to correct interpolation drift.
  useEffect(() => {
    if (!ready) return;
    const id = setInterval(() => {
      try {
        const p = playerRef.current;
        if (!p) return;
        timeRef.current.polledTime = p.getCurrentTime?.() ?? timeRef.current.polledTime;
        timeRef.current.polledAt = performance.now();
        timeRef.current.rate = p.getPlaybackRate?.() ?? 1;
      } catch { /* ignore */ }
    }, 200); // 5 Hz correction
    return () => clearInterval(id);
  }, [ready]);

  return {
    ready,
    error,
    signalError: () => setError(true),
    play: () => {
      try {
        playerRef.current?.playVideo();
        // Optimistically mark as playing so interpolation starts immediately.
        timeRef.current.playing = true;
        timeRef.current.polledAt = performance.now();
      } catch { /* ignore */ }
    },
    pause: () => {
      try {
        playerRef.current?.pauseVideo();
        timeRef.current.playing = false;
      } catch { /* ignore */ }
    },
    seek: (t) => {
      try {
        playerRef.current?.seekTo(t, true);
        timeRef.current.polledTime = t;
        timeRef.current.polledAt = performance.now();
      } catch { /* ignore */ }
    },
    /**
     * Smooth interpolated time: linearly extrapolates between YT polls using
     * wall-clock delta × playback rate. Eliminates chord-pill jitter when YT
     * only updates its internal clock every 200-250 ms.
     */
    getCurrentTime: () => {
      const st = timeRef.current;
      if (!st.polledAt) {
        try { return playerRef.current?.getCurrentTime?.() ?? 0; } catch { return 0; }
      }
      if (!st.playing) return st.polledTime;
      const dtMs = performance.now() - st.polledAt;
      return st.polledTime + (dtMs / 1000) * st.rate;
    },
    setPlaybackRate: (r) => {
      try {
        playerRef.current?.setPlaybackRate?.(r);
        timeRef.current.rate = r;
      } catch { /* ignore */ }
    },
  };
}
